import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface PoLineItem {
  description: string;
  qty: number;
  unitPriceCents: number;
  taxRate?: number;
}

export interface PurchaseOrder {
  id: string;
  workspace_id: string;
  entity_id: string | null;
  vendor_id: string | null;
  po_number: string;
  issue_date: string | null;
  currency: string;
  subtotal_cents: string;
  tax_cents: string;
  total_cents: string;
  status: 'open' | 'closed' | 'cancelled';
  line_items: PoLineItem[];
  notes: string | null;
  owner_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliveryNote {
  id: string;
  workspace_id: string;
  po_id: string | null;
  vendor_id: string | null;
  delivery_number: string | null;
  received_date: string | null;
  currency: string;
  total_cents: string;
  line_items: PoLineItem[];
  inspection_status: 'pending' | 'accepted' | 'rejected';
  inspected_by: string | null;
  inspected_at: string | null;
  notes: string | null;
  owner_user_id: string | null;
  created_at: string;
}

export interface MatchResult {
  matchStatus: 'no_po' | 'matched' | 'partial' | 'mismatch';
  detail:
    | {
        poTotalCents: number;
        invoiceTotalCents: number;
        receivedCents: number;
        toleranceCents: number;
        deltaVsPoCents: number;
        acceptedDeliveries: number;
        reason: string;
      }
    | Record<string, never>;
}

export function usePurchaseOrders(wsId: string | undefined, status?: string) {
  return useQuery<PurchaseOrder[]>({
    queryKey: ['purchase-orders', wsId, status ?? ''],
    enabled: !!wsId,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      return (await api.get<PurchaseOrder[]>(`/workspaces/${wsId}/purchase-orders?${params.toString()}`)).data;
    },
  });
}

export interface CreatePoBody {
  poNumber: string;
  vendorId?: string;
  issueDate?: string;
  currency?: string;
  lineItems: PoLineItem[];
  notes?: string;
}

export function useCreatePurchaseOrder(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePoBody) =>
      (await api.post<PurchaseOrder>(`/workspaces/${wsId}/purchase-orders`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders', wsId] }),
  });
}

export function useDeliveryNotes(wsId: string | undefined, poId?: string) {
  return useQuery<DeliveryNote[]>({
    queryKey: ['delivery-notes', wsId, poId ?? ''],
    enabled: !!wsId,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (poId) params.set('poId', poId);
      return (await api.get<DeliveryNote[]>(`/workspaces/${wsId}/delivery-notes?${params.toString()}`)).data;
    },
  });
}

export interface CreateDeliveryBody {
  poId?: string;
  vendorId?: string;
  deliveryNumber?: string;
  receivedDate?: string;
  currency?: string;
  lineItems: PoLineItem[];
  notes?: string;
}

export function useCreateDeliveryNote(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateDeliveryBody) =>
      (await api.post<DeliveryNote>(`/workspaces/${wsId}/delivery-notes`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-notes', wsId] }),
  });
}

export function useUpdatePurchaseOrder(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<CreatePoBody> & { status?: string } }) =>
      (await api.patch<PurchaseOrder>(`/workspaces/${wsId}/purchase-orders/${id}`, patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
    },
  });
}

export function useUpdateDeliveryNote(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<CreateDeliveryBody> }) =>
      (await api.patch<DeliveryNote>(`/workspaces/${wsId}/delivery-notes/${id}`, patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-notes', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
    },
  });
}

export function useDeletePurchaseOrder(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/purchase-orders/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders', wsId] }),
  });
}

export function useDeleteDeliveryNote(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/delivery-notes/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-notes', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
    },
  });
}

export function useInspectDelivery(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'accepted' | 'rejected' }) =>
      (await api.post<DeliveryNote>(`/workspaces/${wsId}/delivery-notes/${id}/inspect`, { decision })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-notes', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
    },
  });
}

/** Link an AP invoice to a PO (poId) and run the 3-way match. */
export function useMatchApInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ apId, poId }: { apId: string; poId: string | null }) =>
      (await api.post<MatchResult>(`/workspaces/${wsId}/ap-invoices/${apId}/match`, { poId })).data,
    onSuccess: (_, { apId }) => {
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoice', wsId, apId] });
    },
  });
}
